import { useEffect, useState } from "react";
import type { MessageApiResponse } from "@services/message-service/types";
import { getMessages } from "@services/messageApi";
import { useQuery } from "@tanstack/react-query";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";
import { ChatContainer, MessagesArea } from "./Chat.styles";
import MessageBox from "./MessageBox";
import MessageBubble from "./MessageBubble";
import type { EntityType } from "./utils";

interface ChatProps {
  isDisabled: boolean;
  entityId: string;
  entityType: EntityType;
  newMessage: string;
  secondRelatedEntityId?: string;
  setNewMessage: (message: string) => void;
}

const Chat = ({
  isDisabled,
  entityId,
  entityType,
  newMessage,
  secondRelatedEntityId,
  setNewMessage,
}: ChatProps) => {
  const [shouldRefetch, setShouldRefetch] = useState(false);

  const {
    data: messages,
    isLoading,
    refetch,
  } = useQuery<MessageApiResponse[]>({
    queryKey: ["getMessages"],
    queryFn: () => {
      return getMessages({
        entityId,
        entityType,
      });
    },
    // Options to re-fetch on every mount:
    staleTime: 0,
    refetchOnMount: true,
  });

  useEffect(() => {
    if (shouldRefetch) {
      refetch();
      setShouldRefetch(false);
    }
  }, [shouldRefetch, refetch]);

  if (isLoading) {
    return <LoadingOverlay loading={isLoading} />;
  }

  return (
    <div>
      <ChatContainer>
        <MessagesArea>
          {messages && messages.map((msg) => <MessageBubble key={msg.id} message={msg} />)}
        </MessagesArea>
        <MessageBox
          isDisabled={isDisabled}
          entityType={entityType}
          entityId={entityId}
          newMessage={newMessage}
          secondRelatedEntityId={secondRelatedEntityId}
          setNewMessage={setNewMessage}
          setShouldRefetch={setShouldRefetch}
        />
      </ChatContainer>
    </div>
  );
};

export default Chat;
